/**
 * P3.14 · 错误翻译层（最小可行 · 纯 util · 不改 schema）
 *
 * 把 Harness 发的 `error_detail: "${code}: ${message}"` 字符串翻译成用户可读
 * 的中文文案 + 建议动作 + 重试性标志。SPA / Problems tab / Agents tab / 重试
 * 按钮 都消费这个 API。
 *
 * **v0.1 9 类 user-facing codes**（tasks.md P3.14 DoD）：
 *
 * | code | 触发 | 建议动作 | retryable |
 * |---|---|---|---|
 * | rate_limit_exceeded | Anthropic 429 / "rate limit" | 等 60s 再试 | true |
 * | context_length_exceeded | "context length" / "too many tokens" | 压缩 / 拆 session | false |
 * | api_auth_failed | "authentication" / "invalid api key" | 检查 secret | false |
 * | allowlist_violation | E_ALLOWLIST_VIOLATION | 看 audit · 调 agent prompt | false |
 * | subprocess_crashed | E_SPAWN_FAILED / E_PROCESS_DIED / E_PROTOCOL | 重试 · 看 harness log | true |
 * | network_timeout | E_SPAWN_TIMEOUT / E_MCP_CONNECT_FAILED / "turn timeout" | 检查网络 · 重试 | true |
 * | budget_exceeded | budget_guardrail_pause | 升预算 · 详 admin | false |
 * | approval_timeout | waiting_human_timeout | 重试 · 或调 on_approval_timeout | true |
 * | policy_denied | policy.denied | 查 policy matrix · 调权限 | false |
 *
 * 真源：`docs/tasks.md` P3.14
 */

export type ErrorKind =
  | 'rate_limit_exceeded'
  | 'context_length_exceeded'
  | 'api_auth_failed'
  | 'allowlist_violation'
  | 'subprocess_crashed'
  | 'network_timeout'
  | 'budget_exceeded'
  | 'approval_timeout'
  | 'policy_denied'
  | 'unknown';

export interface TranslatedError {
  /** 归一化后的用户向 code · 9 + unknown */
  kind: ErrorKind;
  /** 原始 harness/backend code（如 E_PROCESS_DIED）· 调试可见 */
  rawCode: string;
  /** 原始 message · 折叠"详情"展示 */
  rawMessage: string;
  /** 中文翻译文案 · 1-2 句简明用户语言 */
  messageZh: string;
  /** 建议动作 · 1 句话指引下一步 */
  suggestedAction: string;
  /** 是否可重试（决定 SPA 重试按钮是否亮起）*/
  retryable: boolean;
}

/**
 * 把 Harness 上报的 `error_detail: "${code}: ${message}"` 翻译成 TranslatedError。
 *
 * 输入兼容：
 * - "E_PROCESS_DIED: turn timeout 60000ms · subprocess killed"
 * - "E_ALLOWLIST_VIOLATION: non-mcp tool_use during turn: bash"
 * - "Anthropic API error: rate limit exceeded · retry after 60s"
 * - 任意其他字符串 → kind=unknown · rawCode + rawMessage 留原文
 *
 * @param detail Harness 发的 error_detail 字符串 · 可为空 / undefined
 * @returns TranslatedError · 永远不抛
 */
export function translateError(detail: string | null | undefined): TranslatedError {
  const safe = (detail ?? '').trim();
  if (safe.length === 0) {
    return {
      kind: 'unknown',
      rawCode: '',
      rawMessage: '',
      messageZh: '未知错误',
      suggestedAction: '查看 harness / backend log 获取详情',
      retryable: false,
    };
  }

  // Parse "${code}:${message}" 或 "${code}: ${message}" 或裸 "${code}:" ·
  // code 必须是合法 identifier（字母数字下划线 · 不能含 '.' / 空格）·
  // 否则当无 code · 整串当 message。
  const colonIdx = safe.indexOf(':');
  let rawCode = '';
  let rawMessage = safe;
  if (colonIdx > 0) {
    const candidateCode = safe.slice(0, colonIdx);
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(candidateCode)) {
      rawCode = candidateCode;
      rawMessage = safe.slice(colonIdx + 1).trim();
    }
  }

  const kind = classify(rawCode, rawMessage);
  return {
    kind,
    rawCode,
    rawMessage,
    ...PRESETS[kind],
  };
}

interface Preset {
  messageZh: string;
  suggestedAction: string;
  retryable: boolean;
}

/** 9 类 + unknown 的 zh 文案 · 调 UI 改这里 */
const PRESETS: Record<ErrorKind, Preset> = {
  rate_limit_exceeded: {
    messageZh: 'Anthropic API 调用受限',
    suggestedAction: '等待 60 秒后点重试 · 或换 model · 或检查 API key 余额',
    retryable: true,
  },
  context_length_exceeded: {
    messageZh: '上下文超过模型容量',
    suggestedAction: '在 chat 里精简上下文 · 或拆分到新 session · 不可直接重试',
    retryable: false,
  },
  api_auth_failed: {
    messageZh: 'Anthropic API 鉴权失败',
    suggestedAction: '检查 harness vault 的 anthropic_api_key · 重 set-secret',
    retryable: false,
  },
  allowlist_violation: {
    messageZh: 'Agent 试图调用未授权工具',
    suggestedAction: '查 audit log 看违规工具 · 调整 agent system_prompt 或 policy matrix',
    retryable: false,
  },
  subprocess_crashed: {
    messageZh: 'Claude / Codex 子进程异常退出',
    suggestedAction: '点重试 · 若反复出现查 harness 本地 log',
    retryable: true,
  },
  network_timeout: {
    messageZh: '网络超时 · 子进程或 MCP 连接卡住',
    suggestedAction: '检查 harness 网络连通性 · 点重试',
    retryable: true,
  },
  budget_exceeded: {
    messageZh: '本会话 / workspace 预算已用尽',
    suggestedAction: 'admin 在配置面板提升 budget · 或等次日预算重置',
    retryable: false,
  },
  approval_timeout: {
    messageZh: '人工审批超时未答',
    suggestedAction: '点重试触发新 approval · 或在 agent 配置改 on_approval_timeout',
    retryable: true,
  },
  policy_denied: {
    messageZh: 'Policy matrix 拒绝了本次工具调用',
    suggestedAction: '查 audit 看具体被拒工具 · admin 调 policy 或 agent capabilities',
    retryable: false,
  },
  unknown: {
    messageZh: '未识别的错误',
    suggestedAction: '查 audit 详情 / harness log · 如需要联系 admin',
    retryable: false,
  },
};

/** 主分类函数 · code 命中优先于 message pattern · 未匹配走 message · 都没就 unknown */
function classify(rawCode: string, rawMessage: string): ErrorKind {
  const codeKind = CODE_MAP[rawCode];
  if (codeKind) return codeKind;

  const msgLower = rawMessage.toLowerCase();
  for (const [pattern, kind] of MESSAGE_PATTERNS) {
    if (pattern.test(msgLower)) return kind;
  }
  return 'unknown';
}

/** 原始 code → user-facing kind */
const CODE_MAP: Record<string, ErrorKind> = {
  E_ALLOWLIST_VIOLATION: 'allowlist_violation',
  E_SPAWN_FAILED: 'subprocess_crashed',
  E_PROCESS_DIED: 'subprocess_crashed',
  E_PROTOCOL: 'subprocess_crashed',
  E_SPAWN_TIMEOUT: 'network_timeout',
  E_MCP_CONNECT_FAILED: 'network_timeout',
  E_POOL_ACQUIRE: 'subprocess_crashed',
  // 来自 backend policy enforcement（P2.5 落地）
  policy_denied: 'policy_denied',
  // 来自 backend budget guardrail（P3.3.c 落地）
  budget_guardrail_pause: 'budget_exceeded',
  // 来自 backend approval timeout（P3.9 落地）
  waiting_human_timeout: 'approval_timeout',
};

/**
 * Message 模糊匹配 · 给没明确 code 的 Anthropic API 错误打分类。
 * 先到先得 · 顺序敏感（rate limit 优先于通用 401）。
 */
const MESSAGE_PATTERNS: ReadonlyArray<readonly [RegExp, ErrorKind]> = [
  [/\brate[\s_-]?limit\b|\b429\b|too many requests/, 'rate_limit_exceeded'],
  [/\bcontext[\s_-]?length\b|\btoo many tokens\b|\bmax[_\s-]?tokens\b/, 'context_length_exceeded'],
  [/\bauthentic|\binvalid[_\s-]?api[_\s-]?key\b|\b401\b|\bunauthor/, 'api_auth_failed'],
  [/\bturn timeout\b|\bsubprocess killed\b/, 'network_timeout'],
];

/**
 * 给已知 retryable 错误生成 UI 重试按钮 hint。
 * @returns true 显示"重试" · false 仅显示"查看详情"
 */
export function isRetryable(detail: string | null | undefined): boolean {
  return translateError(detail).retryable;
}
